<script setup>
import { computed } from 'vue';

// The scoreboard: a light panel in the app's Clean & Minimal system, with the SPORT'S
// OWN PHOTO as a soft backdrop (heavy white overlay keeps digits readable and the panel
// on-theme). Brand red appears ONLY on the active turn side — red = what matters now.
// Used identically by the scorer, spectators and the admin preview.
const props = defineProps({
  state: { type: Object, default: null },
  homeName: { type: String, default: 'Home' },
  awayName: { type: String, default: 'Away' },
  schema: { type: Object, default: null },
  backgroundUrl: { type: String, default: null }, // sport image (large)
  // scorer's board: tapping a side hands it the serve/table/jack (correction event)
  interactive: { type: Boolean, default: false },
});

const emit = defineEmits(['switch-side']);

const path = computed(() => props.state?.path ?? []);
const rootScore = computed(() => path.value[0] ?? null);
const leaf = computed(() => path.value[path.value.length - 1] ?? null);


// deuce / golden point / break — one label used by both placements
const statusLabel = computed(() =>
    counter.value && counter.value.currentBreak > 0
        ? `Break ${counter.value.currentBreak}`
        : (leaf.value?.statusDisplay ?? null));const middle = computed(() => path.value.slice(1, -1));

// at completion the engine pops the whole path — the final match score (frames/sets won)
// lives in completedUnits under the root unit
const finalRoot = computed(() => {
  if (!props.state?.completed) return null;
  const rootName = props.schema?.rootUnit;
  const list = rootName ? props.state?.completedUnits?.[rootName] : null;
  return list?.length ? list[list.length - 1] : null;
});

const completedLeafGroups = computed(() => {
  const completed = props.state?.completedUnits ?? {};
  const gamesFrom = props.schema?.resultMapping?.gamesFrom;
  if (!gamesFrom || !completed[gamesFrom]) return [];
  return completed[gamesFrom];
});

const counter = computed(() => {
  const counters = props.state?.counters ?? {};
  const key = Object.keys(counters)[0];
  return key ? { key, ...counters[key] } : null;
});

const isCountdown = computed(() => counter.value?.homeRemaining != null);

const turnSide = computed(() => (props.state?.completed ? null : props.state?.currentTurnSide ?? null));

// action wording per schema role so the highlight reads naturally per sport
const roleActionLabel = computed(() => {
  switch (props.schema?.turns?.role) {
    case 'thrower': return 'Throwing';
    case 'server': return 'Serving';
    case 'jack': return 'Holds the jack';
    case 'break': return 'At the table';
    default: return 'Their turn';
  }
});

const turnChipLabel = computed(() => {
  const server = props.state?.currentServerNumber ? ` · S${props.state.currentServerNumber}` : '';
  return `${roleActionLabel.value}${server}`;
});

// tap a side to hand it the role — only on the scorer's board, only while live
const canSwitch = computed(() =>
    props.interactive && !!props.schema?.turns && !props.state?.completed);

const switchTooltip = (side) => {
  const name = side === 'home' ? props.homeName : props.awayName;
  switch (props.schema?.turns?.role) {
    case 'thrower': return `Make ${name} the thrower`;
    case 'server': return `Make ${name} the server`;
    case 'jack': return `Give ${name} the jack`;
    case 'break': return `Put ${name} at the table`;
    default: return `Give ${name} the turn`;
  }
};

const onSideClick = (side) => {
  if (!canSwitch.value || turnSide.value === side) return;
  emit('switch-side', side);
};

const unitLabel = (unitName) => props.schema?.units?.[unitName]?.label ?? unitName;

// the root line counts CHILD units won — say so: "Frames", "Sets", "Legs" (aggregate
// roots sum child points/shots instead, so "Total")
const rootCountLabel = computed(() => {
  const rootUnit = props.schema?.units?.[rootScore.value?.unit];
  if (!rootUnit) return unitLabel(rootScore.value?.unit ?? '');
  if (rootUnit.winCondition?.type === 'aggregate') return 'Total';
  return rootUnit.child ? `${unitLabel(rootUnit.child)}s` : unitLabel(rootScore.value.unit);
});

const leafDisplay = (side) => {
  // match over: the big digits are the final match score (frames/sets won)
  if (finalRoot.value) return side === 'home' ? finalRoot.value.home : finalRoot.value.away;
  if (!leaf.value) return '0';
  const display = side === 'home' ? leaf.value.homeDisplay : leaf.value.awayDisplay;
  if (display != null) return display;
  if (isCountdown.value) return side === 'home' ? counter.value.homeRemaining : counter.value.awayRemaining;
  return side === 'home' ? leaf.value.home : leaf.value.away;
};

const winnerName = computed(() =>
    props.state?.winnerSide === 'home' ? props.homeName
        : props.state?.winnerSide === 'away' ? props.awayName : null);

const backdropStyle = computed(() =>
    props.backgroundUrl ? { backgroundImage: `url(${props.backgroundUrl})` } : null);
</script>

<template>
  <div v-if="state" class="sb" :class="{'sb--photo': backgroundUrl}">
    <div v-if="backgroundUrl" class="sb-backdrop" :style="backdropStyle" />
    <div class="sb-content">
      <!-- match-level score: sets / frames / legs won, or running aggregate -->
      <div v-if="rootScore && path.length > 1" class="sb-root">
        <span class="sb-root-num">{{ rootScore.home }}</span>
        <span class="sb-root-label">{{ rootCountLabel }}</span>
        <span class="sb-root-num">{{ rootScore.away }}</span>
      </div>

      <!-- intermediate units (e.g. games in the current set) -->
      <div v-for="unit in middle" :key="unit.unit" class="sb-mid">
        {{ unit.home }} &nbsp;·&nbsp; {{ unitLabel(unit.unit) }} {{ unit.index }} &nbsp;·&nbsp; {{ unit.away }}
      </div>

      <!-- the big digits -->
      <div class="sb-grid">
        <div
          class="sb-side"
          :class="{'sb-side--active': turnSide === 'home', 'sb-side--tappable': canSwitch && turnSide !== 'home'}"
          @click="onSideClick('home')"
        >
          <v-tooltip v-if="canSwitch && turnSide !== 'home'" activator="parent" location="top">
            {{ switchTooltip('home') }}
          </v-tooltip>
          <div v-if="turnSide === 'home'" class="sb-turn">{{ turnChipLabel }}</div>
          <div class="sb-name">{{ homeName }}</div>
          <div class="sb-digits">{{ leafDisplay('home') }}</div>
          <div v-if="isCountdown" class="sb-meta">{{ counter.homeDartsThrown }} darts</div>
        </div>

        <div class="sb-center">
          <span v-if="statusLabel" class="sb-status sb-status--inline">{{ statusLabel }}</span>
          <span v-if="statusLabel" class="sb-vs sb-vs--fallback">–</span>
          <span v-if="!statusLabel" class="sb-vs">–</span>
        </div>

        <div
          class="sb-side"
          :class="{'sb-side--active': turnSide === 'away', 'sb-side--tappable': canSwitch && turnSide !== 'away'}"
          @click="onSideClick('away')"
        >
          <v-tooltip v-if="canSwitch && turnSide !== 'away'" activator="parent" location="top">
            {{ switchTooltip('away') }}
          </v-tooltip>
          <div v-if="turnSide === 'away'" class="sb-turn">{{ turnChipLabel }}</div>
          <div class="sb-name">{{ awayName }}</div>
          <div class="sb-digits">{{ leafDisplay('away') }}</div>
          <div v-if="isCountdown" class="sb-meta">{{ counter.awayDartsThrown }} darts</div>
        </div>
      </div>

      <!-- on narrow screens the status chip moves below the digits — the
           centre slot can't fit "Break 6" without wrapping -->
      <div v-if="statusLabel" class="sb-status-row">
        <span class="sb-status">{{ statusLabel }}</span>
      </div>

      <!-- completed games / sets / legs strip -->
      <div v-if="completedLeafGroups.length" class="sb-games">
        <span v-for="game in completedLeafGroups" :key="game.unit + game.index" class="sb-game">
          {{ game.home }}–{{ game.away }}
        </span>
      </div>

      <!-- final result -->
      <div v-if="state.completed" class="sb-final">
        <v-icon icon="mdi-trophy-outline" size="16" class="mr-1" />
        <template v-if="winnerName">{{ winnerName }} wins</template>
        <template v-else>Draw</template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sb {
  position: relative;
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 16px;
  overflow: hidden;
}

/* the sport's own photo as a soft backdrop — heavy white veil keeps it on-theme */
.sb-backdrop {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
}
.sb--photo .sb-backdrop::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94) 0%, rgba(255, 255, 255, 0.82) 100%);
}
.sb-content {
  position: relative;
  padding: 16px 14px 14px;
}

/* match score line: 1 · LEGS · 1 */
.sb-root {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 12px;
  margin-bottom: 2px;
}
.sb-root-num {
  font-size: 20px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--v-theme-text));
}
.sb-root-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
}
.sb-mid {
  text-align: center;
  font-size: 12px;
  color: rgb(var(--v-theme-charcoal));
  margin-bottom: 2px;
  font-variant-numeric: tabular-nums;
}

/* sides */
.sb-grid {
  margin-top: 10px;
  display: flex;
  align-items: stretch;
  gap: 8px;
  margin-top: 10px;
}
.sb-side {
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
  border-radius: 12px;
  padding: 14px 6px 8px;
  position: relative;
}
/* scorer's board: a tappable side takes the serve/table/jack on click */
.sb-side--tappable {
  cursor: pointer;
}
.sb-side--tappable:hover {
  background: rgba(255, 255, 255, 0.6);
  border-radius: 12px;
}

/* the ONE red on the board: the turn chip straddling the active panel's top edge */
.sb-side--active {
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgb(var(--v-theme-borderLight));
}
.sb-name {
  font-size: 13px;
  font-weight: 700;
  color: rgb(var(--v-theme-charcoal));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sb-side--active .sb-name {
  color: rgb(var(--v-theme-text));
}
/* straddles the panel edge: half above, half inside */
.sb-turn {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: rgb(var(--v-theme-primary));
  color: #fff;
  border-radius: 999px;
  padding: 2px 10px;
  white-space: nowrap;
  z-index: 1;
}
.sb-digits {
  font-size: 52px;
  line-height: 1.05;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--v-theme-textSubtle));
}
.sb-side--active .sb-digits {
  color: rgb(var(--v-theme-text));
}
/* no active turn (no turns schema / match over): both sides read full strength */
.sb-grid:not(:has(.sb-side--active)) .sb-digits {
  color: rgb(var(--v-theme-text));
}
.sb-meta {
  font-size: 11px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

/* centre: deuce / golden point / break */
.sb-center {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
}
.sb-status {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-text));
  background: #fff;
  border: 1px solid rgb(var(--v-theme-border));
  border-radius: 999px;
  padding: 4px 10px;
  white-space: nowrap;
}
.sb-vs {
  color: rgb(var(--v-theme-textSubtle));
  font-size: 18px;
}
.sb-vs--fallback {
  display: none;
}
.sb-status-row {
  display: none;
  justify-content: center;
  margin-top: 8px;
}
@media (max-width: 600px) {
  .sb-status--inline { display: none; }
  .sb-vs--fallback { display: inline; }
  .sb-status-row { display: flex; }
}

/* completed games strip */
.sb-games {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}
.sb-game {
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--v-theme-charcoal));
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 999px;
  padding: 2px 10px;
}

.sb-final {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
  font-size: 13px;
  font-weight: 700;
  color: rgb(var(--v-theme-success));
}
</style>
